import React, { useEffect, useContext, useState, useRef } from 'react';
import { useSelectToolbarHeight } from 'app/store/selectors';
import AppToolbar from 'appToolbar/AppToolbar';
import LayoutRenderer from 'layoutRenderer/LayoutRenderer';
import NotificationsList from 'notification/NotificationsList';
import {
    useDispatchClearPendingLayout,
    useDispatchLoadLayoutAction,
} from './store/dispatcher';
import {
    useLayoutSelector,
    useSelectIsLayoutDirty,
    useSelectLayoutManualSave,
} from './store/selectors';
import { useResize } from '@instinet-analytics/core-lib';
import './Layout.scss';
import { useSelectIsAppModeNewportSingleWidget } from 'app/store/general/selectors';
import AppContext from 'app/AppContext';
import { useSelectAppMode } from 'app/store/general/selectors';
import { useBeforeunload } from 'react-beforeunload';
import { useSaveDebouncedLayoutChange } from 'layout/store/hooks';
import Widget from 'widgets/Widget';
import { EmptyLayout } from 'layout/EmptyLayout/EmptyLayout';
import classNames from 'classnames';
import { PendingLayoutOverlayPanel } from 'layout/PendingLayoutOverlayPanel';
import { isLayoutEmpty } from 'layout/LayoutState';
import UnsupportedBrowserBanner from 'app/UnsupportedBrowserBanner';
import {
    useAddLayoutDragSourcePubSub,
    AddLayoutDragSourceContextProvider,
} from '../appToolbar/AddLayoutDragSourcePubSub';
import Services from '../store/Services';
import { useStore } from 'react-redux';
import { PubSub } from '@instinet-analytics/core-lib';
import { useDisplayingNewportVersionWarning } from 'newport/store/hooks';
import { useNewportAppRecommendedVersionNotification } from 'newport/useNewportAppRecommendedVersionNotification';
import { VersionWarnings } from 'app/VersionWarnings/VersionWarnings';
import { useHostBorderPx } from 'embedded/store/hooks.ts';

interface LayoutProps {
    origin: string;
    layout: string;
}

export default function Layout(props: LayoutProps) {
    const { origin, layout: layoutName } = props;
    const { ref, width: windowWidth, height: windowHeight } = useResize();
    const { services } = useContext(AppContext);
    const { widgetsService } = services;

    const dispatchLoadLayoutAction = useDispatchLoadLayoutAction();
    const appToolbarHeight = useSelectToolbarHeight();
    const layout = useLayoutSelector();
    const isAppModeSingleWidget = useSelectIsAppModeNewportSingleWidget();
    const appMode = useSelectAppMode();
    const saveDebouncedLayoutChange = useSaveDebouncedLayoutChange();
    const layoutManualSave = useSelectLayoutManualSave();
    const isLayoutDirty = useSelectIsLayoutDirty();

    const dispatchClearPendingLayout = useDispatchClearPendingLayout();

    const [renderUnsupportedBrowser, setRenderUnsupportedBrowser] =
        useState(false);

    const showingNewportVersionWarning = useDisplayingNewportVersionWarning();

    const hostBorderPx = useHostBorderPx();

    const availableHeight = windowHeight - hostBorderPx * 2;
    const width = windowWidth - hostBorderPx * 2;

    const height = showingNewportVersionWarning
        ? availableHeight - 50
        : availableHeight;

    useNewportAppRecommendedVersionNotification();

    useEffect(
        () => dispatchLoadLayoutAction(origin, layoutName),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useBeforeunload((e: Event) => {
        if (layoutManualSave) {
            if (isLayoutDirty) {
                e.returnValue = true;
            }
        } else {
            saveDebouncedLayoutChange();
        }
    });

    const onOpenAddWidgetModal = useRef(new PubSub<void>());

    const openAddWidgetsModal = () => onOpenAddWidgetModal.current.publish();

    const Loading = <div ref={ref} />;
    if (!layout) {
        return Loading;
    }

    const className = 'AppLayout app-mode-' + appMode;

    // if app mode is Newport single widget then we just want to render a single widget
    if (isAppModeSingleWidget) {
        if (!layout.components) {
            return Loading;
        }
        const widgets = layout.components;
        if (widgets.length === 0) {
            return Loading;
        }

        const widgetToDisplay = widgets[0];

        if (!widgetToDisplay) {
            throw new Error('Layout does not contain a widget component');
        }

        const WidgetComponent = widgetsService.getReactComponentType(
            widgetToDisplay.type
        );
        return (
            <div ref={ref} className={className}>
                <VersionWarnings clientWarnings={true} />
                <Widget
                    component={WidgetComponent}
                    host={'layout'}
                    widgetKey={widgetToDisplay.id}
                    widgetType={widgetToDisplay.type}
                    width={width}
                    height={height}
                />
                <NotificationsList />
            </div>
        );
    }

    const isEmpty = isLayoutEmpty(layout);

    const cssClasses = classNames(className, isEmpty && 'empty');

    const onGlStateChanged = () => {
        dispatchClearPendingLayout();
    };

    const layoutRendererHeight =
        height -
        appToolbarHeight -
        (renderUnsupportedBrowser ? appToolbarHeight : 0);

    return (
        <div ref={ref} className={cssClasses}>
            <AddLayoutDragSourceContextProvider>
                <AppToolbar
                    layoutHeight={height}
                    layoutWidth={width}
                    onOpenAddWidgetModalPubSub={onOpenAddWidgetModal.current}
                />
                <UnsupportedBrowserBanner
                    onRendered={setRenderUnsupportedBrowser}
                />
                <VersionWarnings clientWarnings={false} />
                {!!layout && (
                    <>
                        {isEmpty && (
                            <EmptyLayout
                                openAddWidgetsModal={openAddWidgetsModal}
                            />
                        )}
                        <PendingLayoutOverlayPanel
                            top={appToolbarHeight}
                            height={layoutRendererHeight}
                        />
                        <LayoutRendererWithContext
                            width={width}
                            layoutRendererHeight={layoutRendererHeight}
                            services={services}
                            onGlStateChanged={onGlStateChanged}
                        />
                    </>
                )}
                <NotificationsList />
            </AddLayoutDragSourceContextProvider>
        </div>
    );
}

function LayoutRendererWithContext(props: {
    width: number;
    layoutRendererHeight: number;
    services: Services;
    onGlStateChanged: () => void;
}) {
    const { width, layoutRendererHeight, services, onGlStateChanged } = props;
    const addLayoutDragSourcePubSub = useAddLayoutDragSourcePubSub();
    const store = useStore();

    return (
        <LayoutRenderer
            width={width}
            height={layoutRendererHeight}
            services={services}
            addLayoutDragSourcePubSub={addLayoutDragSourcePubSub}
            onGlStateChanged={onGlStateChanged}
            store={store}
        />
    );
}
